import { Fragment, useEffect, useState } from 'react';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, getDocs, orderBy, query, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { getCurrentWeekRates, convertCurrency, roundToNearest50p, toGBP } from '../../utils/exchangeRates';

// ─── Variant category definitions ─────────────────────────────────────────────

/**
 * Categories where the price depends on a quantity the customer chose.
 * max: 1 means a single price is stored; the quote multiplies by the quantity requested.
 */
const QTY_VARIANTS = {};

/**
 * Categories where the price depends on which options the customer chose.
 * hasBase: true  → there's a "base" price (e.g. flights only / no luggage) that
 *                   option prices ADD to.
 * hasBase: false → price is purely the sum of whichever options were selected.
 * prefsKey: the field on the request addon that holds the selections.
 */
const OPTION_VARIANTS = {};

/**
 * Categories that are priced per session per squad.
 * A single price is stored; the quote multiplies by sessions × squads.
 */
const PER_SQUAD_ITEMS = new Set(['Fixtures', 'Training Sessions']);

function isVariant(name) {
  return !!(QTY_VARIANTS[name] || OPTION_VARIANTS[name]);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const FALLBACK_CATEGORIES = [
  'Hotel & Food', 'Flights', 'Transport', 'Stadium Tour',
  'Fixtures', 'Training Sessions', 'Padel', 'Yoga', 'Waterpark',
];

const BLANK_TIER = { minGuests: '', discountPercent: '' };
const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$' };

/** A free-text priced line staff add to a price code — not tied to a tour category. */
function blankCustomItem() {
  return {
    id: `ci_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    name: '',
    note: '',
    pricingType: 'flat',
    mstCurrency: 'EUR',
    costToMST: '',
    costToCustomer: '',
  };
}

function formatCurrency(n, symbol = '£') {
  if (n === '' || n == null) return '—';
  return `${symbol}${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function defaultPricingType(name) {
  if (name === 'Hotel & Food') return 'per_person_per_night';
  if (name === 'Fixtures' || name === 'Training Sessions' || name === 'Transport') return 'flat';
  return 'per_person';
}

function blankItem(name) {
  const base = {
    categoryName: name,
    pricingType: defaultPricingType(name),
    included: false,
    mstCurrency: name === 'Flights' ? 'GBP' : 'EUR',
    costToMST: '',
    costToCustomer: '',
  };
  if (QTY_VARIANTS[name]) {
    base.variants = Array.from({ length: QTY_VARIANTS[name].max }, (_, i) => ({
      qty: i + 1, costToMST: '', costToCustomer: '',
    }));
  }
  if (name === 'Flights') {
    base.largeCaseCostToMST = '';
    base.largeCaseCostToCustomer = '';
  }
  if (name === 'Transport') {
    base.transportDayPrices = [1, 2, 3, 4].map((d) => ({ day: d, costToMST: '', costToCustomer: '' }));
  }
  return base;
}

function BlankPriceCode(categoryNames) {
  return {
    name: '', description: '', currency: 'GBP', market: 'uk_to_spain',
    targetMargin: '',
    davidsCostPerPerson: '', referralFee: '',
    guestDiscountTiers: [],
    items: categoryNames.map(blankItem),
    customItems: [],
  };
}

/** Merge a saved Firestore item with a freshly-blanked item so new sub-fields appear. */
function mergeItem(saved, blank) {
  const merged = { ...blank, ...saved };
  // Preserve variant arrays: if saved has data use it, otherwise keep blank structure
  if (blank.variants) {
    merged.variants = blank.variants.map((bv) => {
      const sv = (saved.variants || []).find((v) => v.qty === bv.qty);
      return sv ? { ...bv, ...sv } : bv;
    });
  }
  if (blank.optionPrices) {
    merged.optionPrices = blank.optionPrices.map((bo) => {
      const so = (saved.optionPrices || []).find((o) => o.option === bo.option);
      return so ? { ...bo, ...so } : bo;
    });
  }
  if (blank.transportDayPrices) {
    merged.transportDayPrices = blank.transportDayPrices.map((bd) => {
      const sd = (saved.transportDayPrices || []).find((d) => d.day === bd.day);
      return sd ? { ...bd, ...sd } : bd;
    });
  }
  return merged;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function PriceCodes() {
  const { currentUser } = useAuth();
  const [priceCodes, setPriceCodes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [liveRates, setLiveRates] = useState(null);

  useEffect(() => {
    getCurrentWeekRates().then(setLiveRates).catch(() => {});
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'priceCodes'), orderBy('createdAt', 'desc')),
      (snap) => { setPriceCodes(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoading(false); }
    );
    return unsub;
  }, []);

  useEffect(() => {
    getDocs(query(collection(db, 'tourCategories'), orderBy('order', 'asc'))).then((snap) => {
      const names = snap.empty
        ? FALLBACK_CATEGORIES
        : snap.docs.filter((d) => d.data().active !== false && d.data().name !== 'Match Tickets').map((d) => d.data().name);
      setCategories(names);
    }).catch(() => setCategories(FALLBACK_CATEGORIES));
  }, []);

  function openCreate() {
    setEditingId(null);
    setForm(BlankPriceCode(categories));
    setShowForm(true);
    setError('');
  }

  function openEdit(pc) {
    const blankForm = BlankPriceCode(categories);
    const savedMap = Object.fromEntries((pc.items || []).map((it) => [it.categoryName, it]));
    const mergedItems = blankForm.items.map((blank) => {
      const saved = savedMap[blank.categoryName];
      if (!saved) return blank;
      const merged = mergeItem(saved, blank);
      // For old price codes that pre-date per-item mstCurrency, default to the price code's currency
      if (!saved.mstCurrency) merged.mstCurrency = pc.currency || 'GBP';
      return merged;
    });
    setEditingId(pc.id);
    setForm({
      name: pc.name || '',
      description: pc.description || '',
      currency: pc.currency || 'GBP',
      market: pc.market || 'uk_to_spain',
      targetMargin: pc.targetMargin ?? '',
      davidsCostPerPerson: pc.davidsCostPerPerson ?? '',
      referralFee: pc.referralFee ?? '',
      guestDiscountTiers: pc.guestDiscountTiers ? pc.guestDiscountTiers.map((t) => ({ ...t })) : [],
      items: mergedItems,
      customItems: (pc.customItems || []).map((ci) => ({
        ...blankCustomItem(),
        ...ci,
        costToMST: ci.costToMST ?? '',
        costToCustomer: ci.costToCustomer ?? '',
      })),
    });
    setShowForm(true);
    setError('');
  }

  function closeForm() {
    setShowForm(false);
    setForm(null);
    setEditingId(null);
    setError('');
  }

  // ── Form field updaters ────────────────────────────────────────────────────

  /** Calculates the auto-filled customer price from MST cost, applying conversion and margin. */
  function calcAutoFillPrice(mstCost, mstCurrency, customerCurrency, targetMargin) {
    if (!liveRates || mstCost === '' || mstCost == null) return null;
    const num = parseFloat(mstCost);
    if (isNaN(num) || num <= 0) return null;
    const from = mstCurrency || 'EUR';
    const to = customerCurrency;
    const converted = from !== to ? convertCurrency(num, from, to, liveRates) : num;
    const margin = parseFloat(targetMargin) || 0;
    const withMargin = margin > 0 ? converted * (1 + margin / 100) : converted;
    return roundToNearest50p(withMargin).toFixed(2);
  }

  function updateItemField(categoryName, field, value) {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((it) => it.categoryName === categoryName ? { ...it, [field]: value } : it),
    }));
  }

  /** Called onBlur of a standard item's MST cost input — fills customer price. */
  function applyAutoFill(categoryName) {
    setForm((prev) => {
      const item = prev.items.find((it) => it.categoryName === categoryName);
      if (!item) return prev;
      const filled = calcAutoFillPrice(item.costToMST, item.mstCurrency, prev.currency, prev.targetMargin);
      if (filled === null) return prev;
      return { ...prev, items: prev.items.map((it) => it.categoryName === categoryName ? { ...it, costToCustomer: filled } : it) };
    });
  }

  /** Called when mstCurrency changes — re-calculates customer price immediately. */
  function updateTransportDayPrice(day, field, value) {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((it) => {
        if (it.categoryName !== 'Transport') return it;
        const updatedDays = it.transportDayPrices.map((dp) => dp.day === day ? { ...dp, [field]: value } : dp);
        const totalMST      = updatedDays.reduce((s, d) => s + (parseFloat(d.costToMST)      || 0), 0);
        const totalCustomer = updatedDays.reduce((s, d) => s + (parseFloat(d.costToCustomer) || 0), 0);
        return {
          ...it,
          transportDayPrices: updatedDays,
          costToMST:      totalMST      > 0 ? String(totalMST)      : it.costToMST,
          costToCustomer: totalCustomer > 0 ? String(totalCustomer) : it.costToCustomer,
        };
      }),
    }));
  }

  function updateItemMstCurrency(categoryName, value) {
    setForm((prev) => {
      const item = prev.items.find((it) => it.categoryName === categoryName);
      const filled = item ? calcAutoFillPrice(item.costToMST, value, prev.currency, prev.targetMargin) : null;
      return {
        ...prev,
        items: prev.items.map((it) => {
          if (it.categoryName !== categoryName) return it;
          return { ...it, mstCurrency: value, ...(filled !== null ? { costToCustomer: filled } : {}) };
        }),
      };
    });
  }

  function updateVariant(categoryName, qty, field, value) {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((it) => {
        if (it.categoryName !== categoryName) return it;
        return { ...it, variants: it.variants.map((v) => v.qty === qty ? { ...v, [field]: value } : v) };
      }),
    }));
  }

  function applyVariantAutoFill(categoryName, qty) {
    setForm((prev) => {
      const item = prev.items.find((it) => it.categoryName === categoryName);
      if (!item) return prev;
      return {
        ...prev,
        items: prev.items.map((it) => {
          if (it.categoryName !== categoryName) return it;
          return {
            ...it,
            variants: it.variants.map((v) => {
              if (v.qty !== qty) return v;
              const filled = calcAutoFillPrice(v.costToMST, it.mstCurrency, prev.currency, prev.targetMargin);
              return filled !== null ? { ...v, costToCustomer: filled } : v;
            }),
          };
        }),
      };
    });
  }

  function updateOptionPrice(categoryName, option, field, value) {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((it) => {
        if (it.categoryName !== categoryName) return it;
        return { ...it, optionPrices: it.optionPrices.map((op) => op.option === option ? { ...op, [field]: value } : op) };
      }),
    }));
  }

  function applyOptionAutoFill(categoryName, option) {
    setForm((prev) => {
      const item = prev.items.find((it) => it.categoryName === categoryName);
      if (!item) return prev;
      return {
        ...prev,
        items: prev.items.map((it) => {
          if (it.categoryName !== categoryName) return it;
          return {
            ...it,
            optionPrices: it.optionPrices.map((op) => {
              if (op.option !== option) return op;
              const filled = calcAutoFillPrice(op.costToMST, it.mstCurrency, prev.currency, prev.targetMargin);
              return filled !== null ? { ...op, costToCustomer: filled } : op;
            }),
          };
        }),
      };
    });
  }

  // ── Bespoke items ──────────────────────────────────────────────────────────

  function addCustomItem() {
    setForm((prev) => ({
      ...prev,
      customItems: [...(prev.customItems || []), { ...blankCustomItem(), mstCurrency: prev.currency }],
    }));
  }

  function updateCustomItem(itemId, field, value) {
    setForm((prev) => ({
      ...prev,
      customItems: prev.customItems.map((ci) => ci.id === itemId ? { ...ci, [field]: value } : ci),
    }));
  }

  /** Called onBlur of a bespoke item's MST cost — fills the customer price. */
  function applyCustomItemAutoFill(itemId) {
    setForm((prev) => {
      const ci = prev.customItems.find((c) => c.id === itemId);
      if (!ci) return prev;
      const filled = calcAutoFillPrice(ci.costToMST, ci.mstCurrency, prev.currency, prev.targetMargin);
      if (filled === null) return prev;
      return { ...prev, customItems: prev.customItems.map((c) => c.id === itemId ? { ...c, costToCustomer: filled } : c) };
    });
  }

  function updateCustomItemMstCurrency(itemId, value) {
    setForm((prev) => {
      const ci = prev.customItems.find((c) => c.id === itemId);
      const filled = ci ? calcAutoFillPrice(ci.costToMST, value, prev.currency, prev.targetMargin) : null;
      return {
        ...prev,
        customItems: prev.customItems.map((c) => c.id === itemId
          ? { ...c, mstCurrency: value, ...(filled !== null ? { costToCustomer: filled } : {}) }
          : c),
      };
    });
  }

  function removeCustomItem(itemId) {
    setForm((prev) => ({ ...prev, customItems: prev.customItems.filter((ci) => ci.id !== itemId) }));
  }

  // ── Discount tiers ─────────────────────────────────────────────────────────

  function addTier() {
    setForm((prev) => ({ ...prev, guestDiscountTiers: [...prev.guestDiscountTiers, { ...BLANK_TIER }] }));
  }

  function updateTier(idx, field, value) {
    setForm((prev) => {
      const tiers = [...prev.guestDiscountTiers];
      tiers[idx] = { ...tiers[idx], [field]: value };
      return { ...prev, guestDiscountTiers: tiers };
    });
  }

  function removeTier(idx) {
    setForm((prev) => ({ ...prev, guestDiscountTiers: prev.guestDiscountTiers.filter((_, i) => i !== idx) }));
  }

  // ── Save ───────────────────────────────────────────────────────────────────

  async function handleSave(e) {
    e.preventDefault();
    setError('');

    if (!form.name.trim()) { setError('Price code name is required.'); return; }

    for (const t of form.guestDiscountTiers) {
      if (!t.minGuests || !t.discountPercent) {
        setError('All discount tiers must have a guest minimum and discount %.');
        return;
      }
    }

    // Validate included items
    for (const it of form.items.filter((it) => it.included)) {
      if (QTY_VARIANTS[it.categoryName]) {
        for (const v of (it.variants || [])) {
          if (v.costToMST === '' || v.costToCustomer === '') {
            setError(`Enter MST cost and customer price for every ${it.categoryName} quantity option.`);
            return;
          }
        }
      } else if (OPTION_VARIANTS[it.categoryName]) {
        const config = OPTION_VARIANTS[it.categoryName];
        if (config.hasBase && (it.costToMST === '' || it.costToCustomer === '')) {
          setError(`Enter the base price for ${it.categoryName} (flights only / no luggage).`);
          return;
        }
        for (const op of (it.optionPrices || [])) {
          if (op.costToMST === '' || op.costToCustomer === '') {
            setError(`Enter MST cost and customer price for every ${it.categoryName} option.`);
            return;
          }
        }
      } else {
        if (it.costToMST === '' || it.costToCustomer === '') {
          setError(`Enter both MST cost and customer price for ${it.categoryName}.`);
          return;
        }
      }
    }

    for (const ci of (form.customItems || [])) {
      if (!(ci.name || '').trim()) { setError('Every bespoke item needs a name.'); return; }
      if (ci.costToMST === '' || ci.costToCustomer === '') {
        setError(`Enter both MST cost and customer price for "${ci.name.trim()}".`);
        return;
      }
    }

    const serializeItem = (it) => {
      const base = {
        categoryName: it.categoryName,
        pricingType: it.pricingType,
        included: !!it.included,
        mstCurrency: it.mstCurrency || 'EUR',
        costToMST: null,
        costToCustomer: null,
      };
      if (QTY_VARIANTS[it.categoryName]) {
        base.variants = (it.variants || []).map((v) => ({
          qty: v.qty,
          costToMST: it.included && v.costToMST !== '' ? parseFloat(v.costToMST) : null,
          costToCustomer: it.included && v.costToCustomer !== '' ? parseFloat(v.costToCustomer) : null,
        }));
      } else if (OPTION_VARIANTS[it.categoryName]) {
        const config = OPTION_VARIANTS[it.categoryName];
        if (config.hasBase) {
          base.costToMST = it.included && it.costToMST !== '' ? parseFloat(it.costToMST) : null;
          base.costToCustomer = it.included && it.costToCustomer !== '' ? parseFloat(it.costToCustomer) : null;
        }
        base.optionPrices = (it.optionPrices || []).map((op) => ({
          option: op.option,
          costToMST: it.included && op.costToMST !== '' ? parseFloat(op.costToMST) : null,
          costToCustomer: it.included && op.costToCustomer !== '' ? parseFloat(op.costToCustomer) : null,
        }));
      } else if (it.categoryName === 'Transport' && it.transportDayPrices) {
        base.transportDayPrices = it.transportDayPrices.map((dp) => ({
          day: dp.day,
          costToMST: it.included && dp.costToMST !== '' ? parseFloat(dp.costToMST) : null,
          costToCustomer: it.included && dp.costToCustomer !== '' ? parseFloat(dp.costToCustomer) : null,
        }));
        const totalMST = base.transportDayPrices.reduce((s, dp) => s + (dp.costToMST || 0), 0);
        const totalCustomer = base.transportDayPrices.reduce((s, dp) => s + (dp.costToCustomer || 0), 0);
        base.costToMST = it.included && totalMST > 0 ? totalMST : (it.included && it.costToMST !== '' ? parseFloat(it.costToMST) : null);
        base.costToCustomer = it.included && totalCustomer > 0 ? totalCustomer : (it.included && it.costToCustomer !== '' ? parseFloat(it.costToCustomer) : null);
      } else {
        base.costToMST = it.included && it.costToMST !== '' ? parseFloat(it.costToMST) : null;
        base.costToCustomer = it.included && it.costToCustomer !== '' ? parseFloat(it.costToCustomer) : null;
      }
      return base;
    };

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      currency: form.currency,
      market: form.market || 'uk_to_spain',
      targetMargin: form.targetMargin !== '' ? parseFloat(form.targetMargin) : null,
      davidsCostPerPerson: form.davidsCostPerPerson !== '' ? parseFloat(form.davidsCostPerPerson) : 0,
      referralFee: form.referralFee !== '' ? parseFloat(form.referralFee) : 0,
      guestDiscountTiers: form.guestDiscountTiers.map((t) => ({
        minGuests: parseInt(t.minGuests, 10),
        discountPercent: parseFloat(t.discountPercent),
      })).sort((a, b) => a.minGuests - b.minGuests),
      items: form.items.map(serializeItem),
      customItems: (form.customItems || []).map((ci) => ({
        id: ci.id,
        name: ci.name.trim(),
        note: (ci.note || '').trim(),
        pricingType: ci.pricingType || 'flat',
        mstCurrency: ci.mstCurrency || form.currency,
        costToMST: parseFloat(ci.costToMST) || 0,
        costToCustomer: parseFloat(ci.costToCustomer) || 0,
      })),
      updatedAt: serverTimestamp(),
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateDoc(doc(db, 'priceCodes', editingId), payload);
      } else {
        await addDoc(collection(db, 'priceCodes'), {
          ...payload, createdAt: serverTimestamp(), createdBy: currentUser.email, active: true,
        });
      }
      closeForm();
    } catch (err) {
      console.error(err);
      setError('Failed to save price code. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this price code? This cannot be undone.')) return;
    await deleteDoc(doc(db, 'priceCodes', id));
  }

  const symbol = form ? (CURRENCY_SYMBOLS[form.currency] || '£') : '£';

  function conversionHint(costToMST, mstCurrency, customerCurrency) {
    if (!liveRates || costToMST === '' || costToMST == null) return null;
    const num = parseFloat(costToMST);
    if (isNaN(num) || num <= 0) return null;
    const from = mstCurrency || 'EUR';
    const to = customerCurrency;
    const custSym = CURRENCY_SYMBOLS[to] || '£';
    const margin = parseFloat(form?.targetMargin) || 0;

    if (from === to) {
      if (margin <= 0) return null;
      const withMargin = roundToNearest50p(num * (1 + margin / 100));
      return `+${margin}% = ${custSym}${withMargin.toFixed(2)}`;
    }

    const convertedRaw = convertCurrency(num, from, to, liveRates);
    const convertedDisplay = roundToNearest50p(convertedRaw);
    if (margin <= 0) return `≈ ${custSym}${convertedDisplay.toFixed(2)} today`;
    const withMargin = roundToNearest50p(convertedRaw * (1 + margin / 100));
    return `≈ ${custSym}${convertedDisplay.toFixed(2)} converted · ${custSym}${withMargin.toFixed(2)} at ${margin}%`;
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-[20px] font-bold text-[#111827]">Price Codes</h2>
          <p className="text-[13px] text-[#6B7280] mt-1">Create pricing packages that staff can assign to tour requests to auto-generate quotes.</p>
        </div>
        {!showForm && (
          <button onClick={openCreate} className="bg-pb hover:bg-navy text-white text-[13px] font-semibold px-5 py-2.5 rounded-[8px] transition">
            + New Price Code
          </button>
        )}
      </div>

      {/* ─── Form ─────────────────────────────────────────────────────────── */}
      {showForm && form && (
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 mb-8">
          <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-6">
            {editingId ? 'Edit Price Code' : 'New Price Code'}
          </h3>
          <form onSubmit={handleSave} className="space-y-8">

            {/* Basic info */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Name <span className="text-dr">*</span></label>
                <input
                  type="text" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Standard School Package — Spain"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Customer Currency</label>
                <select
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                >
                  <option value="GBP">GBP (£)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="USD">USD ($)</option>
                </select>
                <p className="text-[11px] text-[#9CA3AF] mt-1">Currency the customer pays in — MST costs are set per item</p>
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Tour Direction</label>
                <select
                  value={form.market || 'uk_to_spain'}
                  onChange={(e) => setForm({ ...form, market: e.target.value })}
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                >
                  <option value="uk_to_spain">UK to Spain Tours</option>
                  <option value="spain_to_uk">Spain to UK Tours</option>
                </select>
                <p className="text-[11px] text-[#9CA3AF] mt-1">Which portal this price code appears in when building a quote</p>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Description</label>
                <input
                  type="text" value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="e.g. For school groups — reseller: ABC Tours"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Target Margin (%)</label>
                <input
                  type="number" min="0" max="100" step="0.1"
                  value={form.targetMargin}
                  onChange={(e) => setForm({ ...form, targetMargin: e.target.value })}
                  placeholder="e.g. 20"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
                <p className="text-[11px] text-[#9CA3AF] mt-1">Added on top of converted cost. Leave blank for straight conversion.</p>
              </div>
            </div>

            {/* ── Standard item pricing ───────────────────────────────── */}
            <div>
              <div className="mb-3">
                <p className="text-[13px] font-semibold text-[#374151]">Standard Item Pricing</p>
                <p className="text-[12px] text-[#9CA3AF] mt-0.5">Fixed price per item. Tick to include, set pricing type, then enter costs.</p>
              </div>
              <div className="border border-[#E5E7EB] rounded-[8px] overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-surface border-b border-[#E5E7EB]">
                    <tr>
                      <th className="w-8 px-4 py-2.5"></th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-[#9CA3AF]">Item</th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-[#9CA3AF]">Pricing Type</th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-[#9CA3AF]">Cost to MST</th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-[#9CA3AF]">Price to Customer ({symbol})</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F3F4F6]">
                    {form.items.filter((it) => !isVariant(it.categoryName)).map((item) => (
                      <Fragment key={item.categoryName}>
                      <tr className={item.included ? 'bg-blue-light/20' : ''}>
                        <td className="px-4 py-2.5">
                          <input
                            type="checkbox" checked={!!item.included}
                            onChange={(e) => updateItemField(item.categoryName, 'included', e.target.checked)}
                            className="w-4 h-4 rounded border-[#D1D5DB] text-pb cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="font-medium text-[#111827]">{item.categoryName}</span>
                          {item.categoryName === 'Flights' && (
                            <p className="text-[11px] text-[#9CA3AF] mt-0.5">incl. hand luggage (Jet2)</p>
                          )}
                          {PER_SQUAD_ITEMS.has(item.categoryName) && (
                            <p className="text-[11px] text-[#9CA3AF] mt-0.5">
                              {item.categoryName === 'Fixtures' ? 'per match · per squad' : 'per session · per squad'}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <select
                            value={item.pricingType}
                            onChange={(e) => updateItemField(item.categoryName, 'pricingType', e.target.value)}
                            disabled={!item.included}
                            className="border border-[#E5E7EB] rounded-[6px] px-2 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-pb disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <option value="per_person">Per person</option>
                            <option value="per_person_per_night">Per person per night</option>
                            <option value="flat">Flat fee</option>
                          </select>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-1">
                            <select
                              value={item.mstCurrency || 'EUR'}
                              onChange={(e) => updateItemMstCurrency(item.categoryName, e.target.value)}
                              disabled={!item.included}
                              className="border border-[#E5E7EB] rounded-[6px] px-1 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-pb disabled:opacity-40 disabled:cursor-not-allowed bg-white"
                            >
                              <option value="EUR">€</option>
                              <option value="GBP">£</option>
                              <option value="USD">$</option>
                            </select>
                            {(() => {
                              const hasDays = item.categoryName === 'Transport' && (item.transportDayPrices || []).some((d) => d.costToMST !== '');
                              if (hasDays) {
                                return (
                                  <input
                                    type="number" readOnly
                                    value={item.costToMST}
                                    placeholder="0.00"
                                    title="Auto-calculated from day prices below"
                                    className="w-20 border border-[#E5E7EB] rounded-[6px] px-2 py-1 text-[12px] bg-surface text-[#374151] font-semibold cursor-not-allowed"
                                  />
                                );
                              }
                              return (
                                <input
                                  type="number" min="0" step="0.01"
                                  value={item.costToMST}
                                  onChange={(e) => updateItemField(item.categoryName, 'costToMST', e.target.value)}
                                  onBlur={() => applyAutoFill(item.categoryName)}
                                  disabled={!item.included}
                                  placeholder="0.00"
                                  className="w-20 border border-[#E5E7EB] rounded-[6px] px-2 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-pb disabled:opacity-40 disabled:cursor-not-allowed"
                                />
                              );
                            })()}
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          {(() => {
                            const hasDays = item.categoryName === 'Transport' && (item.transportDayPrices || []).some((d) => d.costToCustomer !== '');
                            if (hasDays) {
                              return (
                                <>
                                  <input
                                    type="number" readOnly
                                    value={item.costToCustomer}
                                    placeholder="0.00"
                                    title="Auto-calculated from day prices below"
                                    className="w-24 border border-[#E5E7EB] rounded-[6px] px-2 py-1 text-[12px] bg-surface text-[#374151] font-semibold cursor-not-allowed"
                                  />
                                  <p className="text-[10px] text-[#9CA3AF] mt-0.5">From day totals</p>
                                </>
                              );
                            }
                            return (
                              <>
                                <input
                                  type="number" min="0" step="0.01"
                                  value={item.costToCustomer}
                                  onChange={(e) => updateItemField(item.categoryName, 'costToCustomer', e.target.value)}
                                  disabled={!item.included}
                                  placeholder="0.00"
                                  className="w-24 border border-[#E5E7EB] rounded-[6px] px-2 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-pb disabled:opacity-40 disabled:cursor-not-allowed"
                                />
                                {item.included && (() => { const hint = conversionHint(item.costToMST, item.mstCurrency || 'EUR', form.currency); return hint ? <p className="text-[10px] text-[#9CA3AF] mt-0.5">{hint}</p> : null; })()}
                              </>
                            );
                          })()}
                        </td>
                      </tr>
                      {item.categoryName === 'Transport' && item.included && (
                        <tr className="bg-surface">
                          <td />
                          <td colSpan={4} className="px-4 py-3">
                            <p className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-[0.7px] mb-2">Day-by-day transport pricing (optional — total auto-calculated)</p>
                            <div className="space-y-1.5">
                              {(item.transportDayPrices || []).map((dp) => {
                                const mstSym = CURRENCY_SYMBOLS[item.mstCurrency || 'EUR'] || '€';
                                return (
                                  <div key={dp.day} className="flex items-center gap-3 flex-wrap">
                                    <span className="text-[12px] font-medium text-[#374151] w-12">Day {dp.day}</span>
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-[11px] text-[#9CA3AF] whitespace-nowrap">MST ({mstSym})</span>
                                      <input
                                        type="number" min="0" step="0.01"
                                        value={dp.costToMST}
                                        onChange={(e) => updateTransportDayPrice(dp.day, 'costToMST', e.target.value)}
                                        placeholder="0.00"
                                        className="w-20 border border-[#E5E7EB] rounded-[6px] px-2 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-pb"
                                      />
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-[11px] text-[#9CA3AF] whitespace-nowrap">Customer ({symbol})</span>
                                      <input
                                        type="number" min="0" step="0.01"
                                        value={dp.costToCustomer}
                                        onChange={(e) => updateTransportDayPrice(dp.day, 'costToCustomer', e.target.value)}
                                        placeholder="0.00"
                                        className="w-20 border border-[#E5E7EB] rounded-[6px] px-2 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-pb"
                                      />
                                    </div>
                                    {(() => { const hint = conversionHint(dp.costToMST, item.mstCurrency || 'EUR', form.currency); return hint ? <span className="text-[10px] text-[#9CA3AF]">{hint}</span> : null; })()}
                                  </div>
                                );
                              })}
                              {(() => {
                                const days = item.transportDayPrices || [];
                                const totalM = days.reduce((s, d) => s + (parseFloat(d.costToMST) || 0), 0);
                                const totalC = days.reduce((s, d) => s + (parseFloat(d.costToCustomer) || 0), 0);
                                if (!totalM && !totalC) return null;
                                const mstSym = CURRENCY_SYMBOLS[item.mstCurrency || 'EUR'] || '€';
                                return (
                                  <div className="flex items-center gap-3 pt-1 border-t border-[#E5E7EB] mt-1">
                                    <span className="text-[12px] font-bold text-[#374151] w-12">Total</span>
                                    <span className="text-[12px] font-bold text-[#111827]">MST: {mstSym}{totalM.toFixed(2)}</span>
                                    <span className="text-[12px] font-bold text-[#111827]">Customer: {symbol}{totalC.toFixed(2)}</span>
                                  </div>
                                );
                              })()}
                            </div>
                          </td>
                        </tr>
                      )}
                      {item.categoryName === 'Flights' && item.included && (
                        <tr className="bg-[#EBF2FC]/40">
                          <td />
                          <td colSpan={4} className="px-4 py-2.5">
                            <div className="flex items-center gap-5 flex-wrap">
                              <p className="text-[12px] font-medium text-[#374151]">Large case (per case · flat group cost):</p>
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] text-[#9CA3AF] whitespace-nowrap">Cost to MST ({CURRENCY_SYMBOLS[item.mstCurrency || 'GBP'] || '£'})</span>
                                <input
                                  type="number" min="0" step="0.01"
                                  value={item.largeCaseCostToMST || ''}
                                  onChange={(e) => updateItemField('Flights', 'largeCaseCostToMST', e.target.value)}
                                  placeholder="0.00"
                                  className="w-20 border border-[#E5E7EB] rounded-[6px] px-2 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-pb"
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] text-[#9CA3AF] whitespace-nowrap">Price to customer ({symbol})</span>
                                <input
                                  type="number" min="0" step="0.01"
                                  value={item.largeCaseCostToCustomer || ''}
                                  onChange={(e) => updateItemField('Flights', 'largeCaseCostToCustomer', e.target.value)}
                                  placeholder="0.00"
                                  className="w-20 border border-[#E5E7EB] rounded-[6px] px-2 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-pb"
                                />
                              </div>
                              <p className="text-[11px] text-[#9CA3AF]">Qty set per request — total is flat for the whole group</p>
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Bespoke items ───────────────────────────────────────── */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[13px] font-semibold text-[#374151]">Bespoke Items</p>
                  <p className="text-[12px] text-[#9CA3AF] mt-0.5">
                    One-off priced lines that aren't a standard tour category. Every bespoke item is added to the quote automatically — the customer doesn't have to request it.
                  </p>
                </div>
                <button
                  type="button" onClick={addCustomItem}
                  className="text-[12px] text-pb hover:text-navy font-medium border border-pb/20 hover:border-pb/40 px-3 py-1.5 rounded-[8px] transition flex-shrink-0"
                >
                  + Add Bespoke Item
                </button>
              </div>
              {(form.customItems || []).length === 0 ? (
                <p className="text-[12px] text-[#9CA3AF] italic">No bespoke items on this price code.</p>
              ) : (
                <div className="space-y-2">
                  {form.customItems.map((ci) => (
                    <div key={ci.id} className="bg-surface border border-[#E5E7EB] rounded-[8px] px-4 py-3">
                      <div className="flex items-start gap-3 flex-wrap">
                        <div className="flex-1 min-w-[180px]">
                          <label className="block text-[11px] font-semibold text-[#9CA3AF] mb-1">Item name</label>
                          <input
                            type="text" value={ci.name}
                            onChange={(e) => updateCustomItem(ci.id, 'name', e.target.value)}
                            placeholder="e.g. Boat trip"
                            className="w-full border border-[#E5E7EB] rounded-[6px] px-2 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-pb"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-[#9CA3AF] mb-1">Pricing type</label>
                          <select
                            value={ci.pricingType}
                            onChange={(e) => updateCustomItem(ci.id, 'pricingType', e.target.value)}
                            className="border border-[#E5E7EB] rounded-[6px] px-2 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-pb bg-white"
                          >
                            <option value="per_person">Per person</option>
                            <option value="per_person_per_night">Per person per night</option>
                            <option value="flat">Flat fee</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-[#9CA3AF] mb-1">Cost to MST</label>
                          <div className="flex items-center gap-1">
                            <select
                              value={ci.mstCurrency || 'EUR'}
                              onChange={(e) => updateCustomItemMstCurrency(ci.id, e.target.value)}
                              className="border border-[#E5E7EB] rounded-[6px] px-1 py-1.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-pb bg-white"
                            >
                              <option value="EUR">€</option>
                              <option value="GBP">£</option>
                              <option value="USD">$</option>
                            </select>
                            <input
                              type="number" min="0" step="0.01"
                              value={ci.costToMST}
                              onChange={(e) => updateCustomItem(ci.id, 'costToMST', e.target.value)}
                              onBlur={() => applyCustomItemAutoFill(ci.id)}
                              placeholder="0.00"
                              className="w-20 border border-[#E5E7EB] rounded-[6px] px-2 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-pb"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-[#9CA3AF] mb-1">Price to Customer ({symbol})</label>
                          <input
                            type="number" min="0" step="0.01"
                            value={ci.costToCustomer}
                            onChange={(e) => updateCustomItem(ci.id, 'costToCustomer', e.target.value)}
                            placeholder="0.00"
                            className="w-24 border border-[#E5E7EB] rounded-[6px] px-2 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-pb"
                          />
                          {(() => { const hint = conversionHint(ci.costToMST, ci.mstCurrency || 'EUR', form.currency); return hint ? <p className="text-[10px] text-[#9CA3AF] mt-0.5">{hint}</p> : null; })()}
                        </div>
                        <button
                          type="button" onClick={() => removeCustomItem(ci.id)}
                          className="text-[#D1D5DB] hover:text-dr transition text-[13px] mt-5"
                          title="Remove bespoke item"
                        >
                          ✕
                        </button>
                      </div>
                      <input
                        type="text" value={ci.note || ''}
                        onChange={(e) => updateCustomItem(ci.id, 'note', e.target.value)}
                        placeholder="Optional note — shown on the quote line"
                        className="w-full mt-2 border border-[#E5E7EB] rounded-[6px] px-2 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-pb bg-white"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Variable item pricing ───────────────────────────────── */}
            <div>
              <div className="mb-3">
                <p className="text-[13px] font-semibold text-[#374151]">Variable Item Pricing</p>
                <p className="text-[12px] text-[#9CA3AF] mt-0.5">
                  Set the price for one session. The quote multiplies this by the quantity the customer requested.
                </p>
              </div>
              <div className="space-y-3">
                {form.items.filter((it) => isVariant(it.categoryName)).map((item) => {
                  const qtyConfig = QTY_VARIANTS[item.categoryName];
                  const optConfig = OPTION_VARIANTS[item.categoryName];

                  return (
                    <div
                      key={item.categoryName}
                      className={`border rounded-[10px] overflow-hidden transition ${item.included ? 'border-pb/20 bg-blue-light/20' : 'border-[#E5E7EB] bg-white'}`}
                    >
                      {/* Card header */}
                      <div className="flex items-center gap-3 px-4 py-3">
                        <input
                          type="checkbox" checked={!!item.included}
                          onChange={(e) => updateItemField(item.categoryName, 'included', e.target.checked)}
                          className="w-4 h-4 rounded border-[#D1D5DB] text-pb cursor-pointer flex-shrink-0"
                        />
                        <span className={`text-[13px] font-semibold flex-1 ${item.included ? 'text-[#111827]' : 'text-[#6B7280]'}`}>
                          {item.categoryName}
                        </span>
                        {item.included && (
                          <>
                            <span className="text-[12px] text-[#9CA3AF]">Pricing type:</span>
                            <select
                              value={item.pricingType}
                              onChange={(e) => updateItemField(item.categoryName, 'pricingType', e.target.value)}
                              className="border border-[#E5E7EB] rounded-[6px] px-2 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-pb"
                            >
                              <option value="per_person">Per person</option>
                              <option value="per_person_per_night">Per person per night</option>
                              <option value="flat">Flat fee</option>
                            </select>
                          </>
                        )}
                        {!item.included && (
                          <span className="text-[12px] text-[#9CA3AF] italic">
                            {qtyConfig
                              ? `Per ${qtyConfig.label} · multiplied by quantity`
                              : `Option-based · ${optConfig.options.length} options`
                            }
                          </span>
                        )}
                      </div>

                      {/* Sub-option price grid (only when included) */}
                      {item.included && (
                        <div className="border-t border-[#E5E7EB] bg-white">
                          {/* Column headers */}
                          <div className="grid grid-cols-3 gap-0 px-4 py-2 bg-surface border-b border-[#F3F4F6]">
                            <span className="text-[11px] font-semibold text-[#9CA3AF]">Option</span>
                            <span className="text-[11px] font-semibold text-[#9CA3AF]">
                              Cost to MST
                              <select
                                value={item.mstCurrency || 'EUR'}
                                onChange={(e) => updateItemMstCurrency(item.categoryName, e.target.value)}
                                className="ml-1.5 border border-[#E5E7EB] rounded-[4px] px-1 py-0.5 text-[11px] focus:outline-none bg-white"
                              >
                                <option value="EUR">€ EUR</option>
                                <option value="GBP">£ GBP</option>
                                <option value="USD">$ USD</option>
                              </select>
                            </span>
                            <span className="text-[11px] font-semibold text-[#9CA3AF]">Price to Customer ({symbol})</span>
                          </div>

                          {/* ─ Quantity-based rows ─ */}
                          {qtyConfig && (item.variants || []).map((v) => (
                            <div key={v.qty} className="grid grid-cols-3 gap-0 px-4 py-2.5 border-b border-[#F3F4F6] last:border-0 items-center">
                              <span className="text-[13px] text-[#374151] font-medium">
                                Per {qtyConfig.label}
                              </span>
                              <div className="pr-4">
                                <input
                                  type="number" min="0" step="0.01"
                                  value={v.costToMST}
                                  onChange={(e) => updateVariant(item.categoryName, v.qty, 'costToMST', e.target.value)}
                                  onBlur={() => applyVariantAutoFill(item.categoryName, v.qty)}
                                  placeholder="0.00"
                                  className="w-full border border-[#E5E7EB] rounded-[6px] px-2 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-pb"
                                />
                              </div>
                              <div className="pr-4">
                                <input
                                  type="number" min="0" step="0.01"
                                  value={v.costToCustomer}
                                  onChange={(e) => updateVariant(item.categoryName, v.qty, 'costToCustomer', e.target.value)}
                                  placeholder="0.00"
                                  className="w-full border border-[#E5E7EB] rounded-[6px] px-2 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-pb"
                                />
                                {(() => { const hint = conversionHint(v.costToMST, item.mstCurrency || 'EUR', form.currency); return hint ? <p className="text-[10px] text-[#9CA3AF] mt-0.5">{hint}</p> : null; })()}
                              </div>
                            </div>
                          ))}

                          {/* ─ Option-based rows (Flights: base + upgrades) ─ */}
                          {optConfig && optConfig.hasBase && (
                            <div className="grid grid-cols-3 gap-0 px-4 py-2.5 border-b border-[#F3F4F6] items-center bg-surface/60">
                              <span className="text-[13px] text-[#374151] font-medium">{optConfig.baseName}</span>
                              <div className="pr-4">
                                <input
                                  type="number" min="0" step="0.01"
                                  value={item.costToMST}
                                  onChange={(e) => updateItemField(item.categoryName, 'costToMST', e.target.value)}
                                  onBlur={() => applyAutoFill(item.categoryName)}
                                  placeholder="0.00"
                                  className="w-full border border-[#E5E7EB] rounded-[6px] px-2 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-pb"
                                />
                              </div>
                              <div className="pr-4">
                                <input
                                  type="number" min="0" step="0.01"
                                  value={item.costToCustomer}
                                  onChange={(e) => updateItemField(item.categoryName, 'costToCustomer', e.target.value)}
                                  placeholder="0.00"
                                  className="w-full border border-[#E5E7EB] rounded-[6px] px-2 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-pb"
                                />
                                {(() => { const hint = conversionHint(item.costToMST, item.mstCurrency || 'EUR', form.currency); return hint ? <p className="text-[10px] text-[#9CA3AF] mt-0.5">{hint}</p> : null; })()}
                              </div>
                            </div>
                          )}

                          {/* ─ Option rows (all option-based categories) ─ */}
                          {optConfig && (item.optionPrices || []).map((op) => (
                            <div key={op.option} className="grid grid-cols-3 gap-0 px-4 py-2.5 border-b border-[#F3F4F6] last:border-0 items-center">
                              <span className="text-[13px] text-[#374151] font-medium flex items-center gap-1.5">
                                {optConfig.hasBase && <span className="text-[#9CA3AF] text-[12px] font-normal">+ add</span>}
                                {optConfig.optionLabels?.[op.option] || op.option}
                              </span>
                              <div className="pr-4">
                                <input
                                  type="number" min="0" step="0.01"
                                  value={op.costToMST}
                                  onChange={(e) => updateOptionPrice(item.categoryName, op.option, 'costToMST', e.target.value)}
                                  onBlur={() => applyOptionAutoFill(item.categoryName, op.option)}
                                  placeholder="0.00"
                                  className="w-full border border-[#E5E7EB] rounded-[6px] px-2 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-pb"
                                />
                              </div>
                              <div className="pr-4">
                                <input
                                  type="number" min="0" step="0.01"
                                  value={op.costToCustomer}
                                  onChange={(e) => updateOptionPrice(item.categoryName, op.option, 'costToCustomer', e.target.value)}
                                  placeholder="0.00"
                                  className="w-full border border-[#E5E7EB] rounded-[6px] px-2 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-pb"
                                />
                                {(() => { const hint = conversionHint(op.costToMST, item.mstCurrency || 'EUR', form.currency); return hint ? <p className="text-[10px] text-[#9CA3AF] mt-0.5">{hint}</p> : null; })()}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Other costs ─────────────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">David's Cost per Person per Night (€ EUR)</label>
                <input
                  type="number" min="0" step="0.01"
                  value={form.davidsCostPerPerson}
                  onChange={(e) => setForm({ ...form, davidsCostPerPerson: e.target.value })}
                  placeholder="0.00"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
                <p className="text-[12px] text-[#9CA3AF] mt-1">Paid in EUR per guest per night. Passed to customer at cost (converted to tour currency).</p>
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Referral Fee ({symbol})</label>
                <input
                  type="number" min="0" step="0.01"
                  value={form.referralFee}
                  onChange={(e) => setForm({ ...form, referralFee: e.target.value })}
                  placeholder="0.00"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
                <p className="text-[12px] text-[#9CA3AF] mt-1">Per player × participants. Paid by MST only — not charged to customer.</p>
              </div>
            </div>

            {/* ── Guest discount tiers ─────────────────────────────────── */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[13px] font-semibold text-[#374151]">Guest Count Discounts</p>
                  <p className="text-[12px] text-[#9CA3AF] mt-0.5">
                    Apply a global % discount to the customer total once a guest threshold is reached. Highest applicable tier is used.
                  </p>
                </div>
                <button
                  type="button" onClick={addTier}
                  className="text-[12px] text-pb hover:text-navy font-medium border border-pb/20 hover:border-pb/40 px-3 py-1.5 rounded-[8px] transition"
                >
                  + Add Tier
                </button>
              </div>
              {form.guestDiscountTiers.length === 0 ? (
                <p className="text-[12px] text-[#9CA3AF] italic">No discount tiers set.</p>
              ) : (
                <div className="space-y-2">
                  {form.guestDiscountTiers.map((tier, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-surface border border-[#E5E7EB] rounded-[8px] px-4 py-3">
                      <span className="text-[13px] text-[#6B7280]">If</span>
                      <input
                        type="number" min="1" value={tier.minGuests}
                        onChange={(e) => updateTier(idx, 'minGuests', e.target.value)}
                        placeholder="20"
                        className="w-20 border border-[#E5E7EB] rounded-[6px] px-2 py-1 text-[13px] focus:outline-none focus:ring-1 focus:ring-pb"
                      />
                      <span className="text-[13px] text-[#6B7280]">or more guests, reduce customer total by</span>
                      <input
                        type="number" min="0.1" max="100" step="0.1" value={tier.discountPercent}
                        onChange={(e) => updateTier(idx, 'discountPercent', e.target.value)}
                        placeholder="10"
                        className="w-20 border border-[#E5E7EB] rounded-[6px] px-2 py-1 text-[13px] focus:outline-none focus:ring-1 focus:ring-pb"
                      />
                      <span className="text-[13px] text-[#6B7280]">%</span>
                      <button type="button" onClick={() => removeTier(idx)} className="ml-auto text-[#D1D5DB] hover:text-dr transition text-[13px]">✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <p className="text-[13px] text-dr bg-red-light border border-dr/20 rounded-[8px] px-4 py-3">{error}</p>
            )}

            <div className="flex gap-3 justify-end border-t border-[#F3F4F6] pt-5">
              <button type="button" onClick={closeForm} className="px-5 py-2 text-[13px] text-[#6B7280] hover:text-[#374151] font-medium">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
                {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Create Price Code'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ─── Price codes list ──────────────────────────────────────────────── */}
      {loading ? (
        <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
      ) : priceCodes.length === 0 && !showForm ? (
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-10 text-center">
          <p className="text-[13px] text-[#9CA3AF] mb-2">No price codes yet.</p>
          <p className="text-[12px] text-[#9CA3AF]">Create your first price code to start generating quotes.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {priceCodes.map((pc) => {
            const sym = CURRENCY_SYMBOLS[pc.currency] || '£';
            const custSym = sym;
            const includedItems = (pc.items || []).filter((it) => it.included);
            const customItems = pc.customItems || [];
            const isExpanded = expandedId === pc.id;
            return (
              <div key={pc.id} className="bg-white rounded-[12px] border border-[#E5E7EB] overflow-hidden">
                <div
                  className="flex items-start justify-between p-5 cursor-pointer hover:bg-surface transition"
                  onClick={() => setExpandedId(isExpanded ? null : pc.id)}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-[15px] font-semibold text-[#111827]">{pc.name}</p>
                      <span className="text-[11px] font-semibold px-[7px] py-[2px] rounded-[5px] bg-[#F3F4F6] text-[#6B7280]">{pc.currency || 'GBP'}</span>
                      <span className="text-[11px] font-semibold px-[7px] py-[2px] rounded-[5px] bg-blue-light text-pb">
                        {(pc.market || 'uk_to_spain') === 'spain_to_uk' ? 'Spain → UK' : 'UK → Spain'}
                      </span>
                    </div>
                    {pc.description && <p className="text-[13px] text-[#6B7280] mt-0.5">{pc.description}</p>}
                    <p className="text-[12px] text-[#9CA3AF] mt-1">
                      {includedItems.length} item{includedItems.length !== 1 ? 's' : ''} included
                      {customItems.length > 0 && ` · ${customItems.length} bespoke item${customItems.length > 1 ? 's' : ''}`}
                      {pc.guestDiscountTiers?.length > 0 && ` · ${pc.guestDiscountTiers.length} discount tier${pc.guestDiscountTiers.length > 1 ? 's' : ''}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                    <button onClick={(e) => { e.stopPropagation(); openEdit(pc); }} className="text-[12px] text-pb hover:text-navy font-medium">Edit</button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(pc.id); }} className="text-[12px] text-dr/70 hover:text-dr font-medium">Delete</button>
                    <span className="text-[#D1D5DB] text-[13px]">{isExpanded ? '▲' : '▼'}</span>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-[#F3F4F6] px-5 py-4 space-y-5 bg-surface/50">
                    {includedItems.length > 0 && (
                      <div>
                        <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-3">Item Pricing</p>
                        <div className="space-y-3">
                          {includedItems.map((it) => {
                            const qtyConfig = QTY_VARIANTS[it.categoryName];
                            const optConfig = OPTION_VARIANTS[it.categoryName];

                            // Standard item
                            if (!qtyConfig && !optConfig) {
                              const mstSym = CURRENCY_SYMBOLS[it.mstCurrency || pc.currency] || custSym;
                              const mstGBP = liveRates && it.costToMST != null ? toGBP(it.costToMST, it.mstCurrency || pc.currency, liveRates) : null;
                              const custGBP = liveRates && it.costToCustomer != null ? toGBP(it.costToCustomer, pc.currency, liveRates) : null;
                              const marginGBP = mstGBP != null && custGBP != null ? custGBP - mstGBP : null;
                              const hasDayPrices = it.categoryName === 'Transport' && (it.transportDayPrices || []).some((d) => d.costToMST);
                              return (
                                <div key={it.categoryName} className="bg-white border border-[#E5E7EB] rounded-[8px] overflow-hidden">
                                  <div className="px-4 py-3 flex items-center gap-6 flex-wrap">
                                    <div className="w-36">
                                      <span className="text-[13px] font-semibold text-[#111827]">{it.categoryName}</span>
                                      {PER_SQUAD_ITEMS.has(it.categoryName) && (
                                        <p className="text-[11px] text-[#9CA3AF]">
                                          {it.categoryName === 'Fixtures' ? 'per match · per squad' : 'per session · per squad'}
                                        </p>
                                      )}
                                    </div>
                                    <span className="text-[12px] text-[#9CA3AF]">
                                      {it.pricingType === 'per_person' ? 'Per person' : it.pricingType === 'per_person_per_night' ? 'Per person/night' : 'Flat'}
                                    </span>
                                    <span className="text-[12px] text-[#6B7280]">MST: <strong className="text-[#111827]">{formatCurrency(it.costToMST, mstSym)}</strong></span>
                                    <span className="text-[12px] text-[#6B7280]">Customer: <strong className="text-[#111827]">{formatCurrency(it.costToCustomer, custSym)}</strong></span>
                                    {marginGBP != null && (
                                      <span className={`text-xs font-medium ml-auto ${marginGBP >= 0 ? 'text-sg' : 'text-dr'}`}>
                                        Margin: {formatCurrency(marginGBP, '£')}
                                      </span>
                                    )}
                                  </div>
                                  {hasDayPrices && (
                                    <div className="border-t border-[#F3F4F6] px-4 py-2.5 bg-surface/50">
                                      <div className="flex flex-wrap gap-x-5 gap-y-1">
                                        {(it.transportDayPrices || []).filter((d) => d.costToMST || d.costToCustomer).map((d) => (
                                          <span key={d.day} className="text-[11.5px] text-[#6B7280]">
                                            Day {d.day}: <strong className="text-[#111827]">{formatCurrency(d.costToMST, mstSym)}</strong> MST · <strong className="text-[#111827]">{formatCurrency(d.costToCustomer, custSym)}</strong> customer
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            // Quantity-variant item
                            if (qtyConfig) {
                              const mstSym = CURRENCY_SYMBOLS[it.mstCurrency || pc.currency] || custSym;
                              return (
                                <div key={it.categoryName} className="bg-white border border-[#E5E7EB] rounded-[8px] overflow-hidden">
                                  <div className="px-4 py-2.5 bg-surface border-b border-[#E5E7EB] flex items-center gap-3">
                                    <span className="text-[13px] font-semibold text-[#111827]">{it.categoryName}</span>
                                    <span className="text-[12px] text-[#9CA3AF]">
                                      {it.pricingType === 'per_person' ? 'Per person' : it.pricingType === 'per_person_per_night' ? 'Per person/night' : 'Flat'} · quantity-based
                                    </span>
                                  </div>
                                  <div className="divide-y divide-[#F3F4F6]">
                                    {(it.variants || []).map((v) => {
                                      const vMstGBP = liveRates && v.costToMST != null ? toGBP(v.costToMST, it.mstCurrency || pc.currency, liveRates) : null;
                                      const vCustGBP = liveRates && v.costToCustomer != null ? toGBP(v.costToCustomer, pc.currency, liveRates) : null;
                                      const marginGBP = vMstGBP != null && vCustGBP != null ? vCustGBP - vMstGBP : null;
                                      return (
                                        <div key={v.qty} className="px-4 py-2 flex items-center gap-6 text-xs">
                                          <span className="text-[#374151] font-medium w-24">Per {qtyConfig.label}</span>
                                          <span className="text-[#6B7280]">MST: <strong className="text-[#111827]">{formatCurrency(v.costToMST, mstSym)}</strong></span>
                                          <span className="text-[#6B7280]">Customer: <strong className="text-[#111827]">{formatCurrency(v.costToCustomer, custSym)}</strong></span>
                                          {marginGBP != null && (
                                            <span className={`font-medium ml-auto ${marginGBP >= 0 ? 'text-sg' : 'text-dr'}`}>
                                              Margin: {formatCurrency(marginGBP, '£')}
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            }

                            // Option-variant item (Flights, Transport, Food)
                            {
                              const mstSym = CURRENCY_SYMBOLS[it.mstCurrency || pc.currency] || custSym;
                              return (
                                <div key={it.categoryName} className="bg-white border border-[#E5E7EB] rounded-[8px] overflow-hidden">
                                  <div className="px-4 py-2.5 bg-surface border-b border-[#E5E7EB] flex items-center gap-3">
                                    <span className="text-[13px] font-semibold text-[#111827]">{it.categoryName}</span>
                                    <span className="text-[12px] text-[#9CA3AF]">
                                      {it.pricingType === 'per_person' ? 'Per person' : it.pricingType === 'per_person_per_night' ? 'Per person/night' : 'Flat'} · option-based
                                    </span>
                                  </div>
                                  <div className="divide-y divide-[#F3F4F6]">
                                    {optConfig.hasBase && (() => {
                                      const bMstGBP = liveRates && it.costToMST != null ? toGBP(it.costToMST, it.mstCurrency || pc.currency, liveRates) : null;
                                      const bCustGBP = liveRates && it.costToCustomer != null ? toGBP(it.costToCustomer, pc.currency, liveRates) : null;
                                      const marginGBP = bMstGBP != null && bCustGBP != null ? bCustGBP - bMstGBP : null;
                                      return (
                                        <div className="px-4 py-2 flex items-center gap-6 text-[12px] bg-surface/50">
                                          <span className="text-[#374151] font-medium w-48">{optConfig.baseName}</span>
                                          <span className="text-[#6B7280]">MST: <strong className="text-[#111827]">{formatCurrency(it.costToMST, mstSym)}</strong></span>
                                          <span className="text-[#6B7280]">Customer: <strong className="text-[#111827]">{formatCurrency(it.costToCustomer, custSym)}</strong></span>
                                          {marginGBP != null && (
                                            <span className={`font-medium ml-auto ${marginGBP >= 0 ? 'text-sg' : 'text-dr'}`}>
                                              Margin: {formatCurrency(marginGBP, '£')}
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })()}
                                    {(it.optionPrices || []).map((op) => {
                                      const opMstGBP = liveRates && op.costToMST != null ? toGBP(op.costToMST, it.mstCurrency || pc.currency, liveRates) : null;
                                      const opCustGBP = liveRates && op.costToCustomer != null ? toGBP(op.costToCustomer, pc.currency, liveRates) : null;
                                      const marginGBP = opMstGBP != null && opCustGBP != null ? opCustGBP - opMstGBP : null;
                                      return (
                                        <div key={op.option} className="px-4 py-2 flex items-center gap-6 text-xs">
                                          <span className="text-[#374151] font-medium w-48">
                                            {optConfig.hasBase && <span className="text-[#9CA3AF] mr-1">+ add</span>}
                                            {op.option}
                                          </span>
                                          <span className="text-[#6B7280]">MST: <strong className="text-[#111827]">{formatCurrency(op.costToMST, mstSym)}</strong></span>
                                          <span className="text-[#6B7280]">Customer: <strong className="text-[#111827]">{formatCurrency(op.costToCustomer, custSym)}</strong></span>
                                          {marginGBP != null && (
                                            <span className={`font-medium ml-auto ${marginGBP >= 0 ? 'text-sg' : 'text-dr'}`}>
                                              Margin: {formatCurrency(marginGBP, '£')}
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            }
                          })}
                        </div>
                      </div>
                    )}

                    {customItems.length > 0 && (
                      <div>
                        <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-3">Bespoke Items</p>
                        <div className="space-y-2">
                          {customItems.map((ci) => {
                            const mstSym = CURRENCY_SYMBOLS[ci.mstCurrency || pc.currency] || custSym;
                            const mstGBP = liveRates && ci.costToMST != null ? toGBP(ci.costToMST, ci.mstCurrency || pc.currency, liveRates) : null;
                            const custGBP = liveRates && ci.costToCustomer != null ? toGBP(ci.costToCustomer, pc.currency, liveRates) : null;
                            const marginGBP = mstGBP != null && custGBP != null ? custGBP - mstGBP : null;
                            return (
                              <div key={ci.id} className="bg-white border border-[#E5E7EB] rounded-[8px] px-4 py-3 flex items-center gap-6 flex-wrap">
                                <div className="w-36">
                                  <span className="text-[13px] font-semibold text-[#111827]">{ci.name}</span>
                                  {ci.note && <p className="text-[11px] text-[#9CA3AF]">{ci.note}</p>}
                                </div>
                                <span className="text-[12px] text-[#9CA3AF]">
                                  {ci.pricingType === 'per_person' ? 'Per person' : ci.pricingType === 'per_person_per_night' ? 'Per person/night' : 'Flat'}
                                </span>
                                <span className="text-[12px] text-[#6B7280]">MST: <strong className="text-[#111827]">{formatCurrency(ci.costToMST, mstSym)}</strong></span>
                                <span className="text-[12px] text-[#6B7280]">Customer: <strong className="text-[#111827]">{formatCurrency(ci.costToCustomer, custSym)}</strong></span>
                                {marginGBP != null && (
                                  <span className={`text-xs font-medium ml-auto ${marginGBP >= 0 ? 'text-sg' : 'text-dr'}`}>
                                    Margin: {formatCurrency(marginGBP, '£')}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-3">
                        {pc.davidsCostPerPerson > 0 && (
                          <div>
                            <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1">David's Cost</p>
                            <p className="text-[13px] text-[#374151]">{formatCurrency(pc.davidsCostPerPerson, '€')} per guest/night <span className="text-[12px] text-[#9CA3AF]">(at cost, paid in EUR)</span></p>
                          </div>
                        )}
                        {pc.referralFee > 0 && (
                          <div>
                            <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1">Referral Fee</p>
                            <p className="text-[13px] text-[#374151]">{formatCurrency(pc.referralFee, sym)} <span className="text-[12px] text-[#9CA3AF]">(internal only)</span></p>
                          </div>
                        )}
                      </div>
                      {pc.guestDiscountTiers?.length > 0 && (
                        <div>
                          <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-2">Discount Tiers</p>
                          <div className="space-y-1">
                            {[...pc.guestDiscountTiers].sort((a, b) => a.minGuests - b.minGuests).map((t, i) => (
                              <p key={i} className="text-[13px] text-[#374151]">
                                {t.minGuests}+ guests → <span className="font-medium text-sg">{t.discountPercent}% off</span>
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
